import React from 'react';
import { Alert, Cards, ColumnLayout, Flash, Icon } from 'AWS-UI-Components-React';
import { getJSClient, JSClient } from '../js_client';
import { extractErrorMessage, formatNumber, scrollIntoView } from '../../utilities';
import { getRandomColor } from "../../utils/graph-utils"

const Recharts = window.Recharts;

const BarChart = Recharts.BarChart;
const Bar = Recharts.Bar;
const XAxis = Recharts.XAxis;
const YAxis = Recharts.YAxis;
const CartesianGrid = Recharts.CartesianGrid;
const Tooltip = Recharts.Tooltip;
const Legend = Recharts.Legend;
const ReferenceLine = Recharts.ReferenceLine;

export class MappingImpact extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mapping: props.mapping,
      loading: true,
      error: false,
    };

    this.cardDefinition = {
      header: (item) => item.name,
      sections: [
        {
          id: 'firstAnswer',
          header: 'First Answer',
          content: (item) => item.firstAnswer,
        },
        {
          id: 'originalBenchmark',
          header: 'Original Benchmark',
          content: (item) => item.originalBenchmark,
        },
        {
          id: 'originalNetAccuracy',
          header: 'Original First Answer Accuracy (Net)',
          content: (item) => <span>{item.originalNetAccuracy} %</span>,
        },
        {
          id: 'newBenchmark',
          header: 'New Benchmark',
          content: (item) => (
            <div className={item.benchmarkLabelStyle}>
              <span>
                {item.newBenchmark} {item.benchmarkDiff && <Icon name={item.benchmarkIcon} />}
              </span>
            </div>
          ),
        },
        {
          id: 'newNetAccuracy',
          header: 'New First Answer Accuracy (Net)',
          content: (item) => (
            <div className={item.netAccuracyLabelStyle}>
              <span>
                {item.newNetAccuracy} % {item.netAccuracyDiff && <Icon name={item.netAccuracyIcon} />}
              </span>
            </div>
          ),
        },
      ],
    };

    this.colorMap = {};
  }

  componentWillMount = () => {
    this.client = getJSClient();
    this.initialClientTasks();
  };

  componentDidMount() {
    scrollIntoView('impact-area');
  }

  initialClientTasks = () => {
    this.client.getMappingImpact(this.onSuccessfulGetImpact, this.onFailureToGetImpact, {
      mapping: this.props.mapping,
    });
  };

  onSuccessfulGetImpact = (data) => {
    let graphData = [];
    let metadata = [];
    let oldData = data.oldComparision;
    let newData = data.newComparision;

    let [oldBenchmarkItems, oldSum] = this.extractItems(
      oldData.benchmarkComposition,
      'Old ',
      metadata,
      'Old Benchmark'
    );
    let [newBenchmarkItems, newSum] = this.extractItems(newData.benchmarkComposition, '', metadata, 'New Benchmark');

    let [newCPItems, cpSum] = this.extractItems(newData.cpComposition, '', metadata, 'First Answer');

    graphData.push(newCPItems);
    graphData.push(oldBenchmarkItems);
    graphData.push(newBenchmarkItems);

    let legendPayload = [];
    let legendSet = {};

    if (metadata.length < 100) {
      for (let i = 0; i < metadata.length; i++) {
        if (!legendSet[metadata[i].label]) {
          legendPayload.push({
            value: metadata[i].label + ' : ' + metadata[i].value + ' M',
            type: 'square',
            color: metadata[i].color,
            id: metadata[i].label,
          });
          legendSet[metadata[i].label] = true;
        }
      }
    } else {
      legendPayload.push({
        value: 'Too Many Items to Display Legend',
      });
    }

    let benchmarkIcon = 'angle-down-double';
    let benchmarkDiff = true;
    let benchmarkLabelStyle = 'awsui-util-status-negative';
    if (newSum > oldSum) {
      benchmarkIcon = 'angle-up-double';
      benchmarkLabelStyle = 'awsui-util-status-positive';
    }
    if (newSum === oldSum) {
      benchmarkDiff = false;
      benchmarkLabelStyle = '';
    }

    let netAccuracyIcon = 'angle-down-double';
    let netAccuracyDiff = true;
    let netAccuracyLabelStyle = 'awsui-util-status-negative';
    let newNetAccuracy = this.getNetAccuracy(cpSum, newSum);
    let oldNetAccuracy = this.getNetAccuracy(cpSum, oldSum);
    if (newNetAccuracy > oldNetAccuracy) {
      netAccuracyIcon = 'angle-up-double';
      netAccuracyLabelStyle = 'awsui-util-status-positive';
    }
    if (newNetAccuracy === oldNetAccuracy) {
      netAccuracyDiff = false;
      netAccuracyLabelStyle = '';
    }

    let items = [
      {
        name: 'Summary',
        firstAnswer: formatNumber(cpSum, 1, 0, true),
        originalBenchmark: formatNumber(oldSum, 1, 0, true),
        newBenchmark: formatNumber(newSum, 1, 0, true),
        originalNetAccuracy: oldNetAccuracy,
        newNetAccuracy: newNetAccuracy,
        benchmarkIcon: benchmarkIcon,
        benchmarkDiff: benchmarkDiff,
        netAccuracyIcon: netAccuracyIcon,
        netAccuracyDiff: netAccuracyDiff,
        netAccuracyLabelStyle: netAccuracyLabelStyle,
        benchmarkLabelStyle: benchmarkLabelStyle,
      },
    ];

    this.setState({
      loading: false,
      error: false,
      data: data,
      graphData: graphData,
      metadata: metadata,
      legendPayload: legendPayload,
      cardItems: items,
    });
    console.log(data);
  };

  getNetAccuracy(cpSum, benchmarkSum) {
    let accuracy = 100 * (1 - Math.abs(Math.abs(cpSum - benchmarkSum) / benchmarkSum));
    if (accuracy < 0 || accuracy > 100) {
      accuracy = 0;
    }
    return formatNumber(accuracy, 1, 2, false);
  }

  extractItems(composition, prefix, metadata, name) {
    let graphItems = {
      name: name,
    };
    let keys = Object.keys(composition);
    let sum = 0;
    for (let i = 0; i < keys.length; i++) {
      let color = this.getRandomColor(keys[i]);
      graphItems[prefix + keys[i]] = formatNumber(composition[keys[i]], 1000000, 2, false);
      sum += composition[keys[i]];
      metadata.push({
        keyName: prefix + keys[i],
        color: color,
        label: keys[i],
        value: formatNumber(composition[keys[i]], 1000000, 2, false),
      });
    }
    return [graphItems, sum];
  }

  getRandomColor(key) {
    let color = this.colorMap[key];
    if (!color) {
      color = getRandomColor()
      this.colorMap[key] = color;
    }
    return color;
  }

  onFailureToGetImpact = (err) => {
    this.setState({ loading: false, error: true, message: extractErrorMessage(err) });
    console.log(err);
  };

  render() {
    return (
      <div>
        <div className="awsui-util-container" id={'impact-area'}>
          {this.state.loading && (
            <div>
              <Flash type="success" loading={true}>
                Loading impact as per latest mappings
              </Flash>
            </div>
          )}
          {this.state.error && !this.state.loading && (
            <Alert header="Error Getting the impact" type="error">
              {this.state.message}
            </Alert>
          )}
          {!this.state.loading && !this.state.error && (
            <div>
              <ColumnLayout columns={3}>
                <div data-awsui-column-layout-root="true">
                  <BarChart width={600} height={500} stackOffset="sign" data={this.state.graphData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis label={{ value: 'Millions USD', angle: -90, position: 'insideLeft' }} />
                    <ReferenceLine y={0} stroke="#000" />
                    <Legend payload={this.state.legendPayload} />
                    <Tooltip />
                    {this.state.metadata &&
                      this.state.metadata.map((value, index) => {
                        return (
                          <Bar
                            key={value.keyName}
                            name={value.label}
                            stackId="all"
                            dataKey={value.keyName}
                            fill={value.color}
                            unit=" M"
                          />
                        );
                      })}
                  </BarChart>
                  <div> </div>
                  <Cards cardDefinition={this.cardDefinition} items={this.state.cardItems} />
                </div>
              </ColumnLayout>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default MappingImpact;



// WEBPACK FOOTER //
// ./src/components/mapper/mapping_impact.jsx